// @ts-check
import { defineStore } from "pinia";
import authUtils from "../../../sano-ui-library/src/utils/auth.utils";
import UsersApi from "@/data/api-layer/usersApi";
import type { Ref } from "vue";

const useAuthStore = defineStore("authStore", () => {
   const jwt: Ref<string | null> = ref(null);
   const isRefreshingToken = ref<boolean>(false);

   function getIsRefreshingToken(): boolean {
      return isRefreshingToken.value;
   }

   function setIsRefreshingToken(value: boolean) {
      isRefreshingToken.value = value;
   }

   function getToken(): string | null {
      return jwt.value ? jwt.value : null;
   }

   function setToken(token: string): void {
      if (token) jwt.value = token;
   }

   function destroyToken(): void {
      jwt.value = null;
   }

   /* Unlike isAuthTokenAndDCValid, authTokenExists will still return true while e.g. we check if a user's session has expired
    ** so it is good to use for display purposes - for example the "logout" & "login" buttons should not flicker in & out of existence while we check the current session status */
   const authTokenExists = computed(() => {
      if (import.meta.env.SSR) return false;
      return !!jwt.value;
   });

   /* We need to know if a user is being impersonated by e.g. a healthcare provider to show a warning notice */
   function getIsImpersonating() {
      const dt = authUtils.getDecodedTokenIncExpired(jwt.value);
      return !!dt.impersonator_email;
   }

   /** refresh the token using API endpoint.
    * preventDeleteTokenOnRefreshFail is used during login.  On login, a token is set. If a device code is needed, refresh will be called on the route change /login > /device-code
    * refresh requests fail when no device code is present, and if we destroyed the token, route guards would prevent us reaching the device code page
    */
   function refreshTheToken(preventDeleteTokenOnRefreshFail: boolean = false): Promise<any> {
      if (!getIsRefreshingToken()) {
         setIsRefreshingToken(true);
         return new Promise((resolve, reject) => {
            UsersApi.requestTokenRefresh()
               .then((response) => {
                  if (response.status === 200) {
                     setToken(response.headers.token);
                  } else {
                     if (!preventDeleteTokenOnRefreshFail) {
                        destroyToken();
                     }
                  }
                  resolve(response);
               })
               .catch((error) => {
                  console.error("Failed to refreshTheToken", error);
                  destroyToken();
                  reject(error);
               })
               .finally(() => {
                  setIsRefreshingToken(false);
               });
         });
      }
   }

   async function getStatusAfterRefreshCompletion(): Promise<"valid" | "expired" | null> {
      const checkIntervalMs = 250;
      const maxWaitTimeMs = 10000;

      return new Promise((resolve) => {
         const maxChecks = maxWaitTimeMs / checkIntervalMs;
         let checksDone = 0;

         // check every {checkIntervalMs} if the token has finishined refreshing
         const interval = setInterval(() => {
            checksDone += 1;

            // if the token is not being refreshed, we can resolve
            if (!getIsRefreshingToken()) {
               clearInterval(interval);
               resolve(authUtils.getTokenStatus(jwt.value));
            }
            // if the token is being refreshed and the max time has passed, we reject
            else if (checksDone >= maxChecks) {
               clearInterval(interval);
               console.error(`Refresh completion request exceeded ${maxWaitTimeMs / 1000} seconds duration`);
               resolve(null);
            }
         }, checkIntervalMs);
      });
   }

   /** this function is used for admins with appropriate permissions (e.g. HCPs) impersonating a user */
   function completeAccess(token: string) {
      return new Promise((resolve, reject) => {
         UsersApi.completeImpersonationAccess(token)
            .then((response) => {
               setToken(response.headers.token);
               resolve(response);
            })
            .catch((error) => {
               reject(error);
            });
      });
   }

   return {
      getIsRefreshingToken,
      setIsRefreshingToken,
      getToken,
      setToken,
      destroyToken,
      authTokenExists,
      getIsImpersonating,
      refreshTheToken,
      getStatusAfterRefreshCompletion,
      completeAccess,

      getTokenStatus: () => authUtils.getTokenStatus(jwt.value),
      getUserEmail: () => authUtils.getUserEmail(jwt.value),
      getUserId: () => authUtils.getUserId(jwt.value),
      hasPermission: (permissions: string | string[], everyPerm?: boolean) => {
         const everyPermNeeded = !!everyPerm;
         return authUtils.hasPermission(permissions, everyPermNeeded, jwt.value);
      },
      isAuthTokenAndDCValid: () => {
         if (import.meta?.env?.SSR) return false;
         return authUtils.isAuthTokenAndDCValid(jwt.value, "participant-device");
      },
   };
});

export { useAuthStore };
